import Col from "components/ui-elements/Col";
import DataPagination from "components/datatable/DataPagination";
import PopupDialog from "components/dialog/PopupDialog";
import useAxiosFunction from "hooks/useAxiosFunction";
import React from "react";

function normalizeList(value) {
  if (!value) return [];
  const items = Array.isArray(value) ? value : String(value).split(",");
  return items.map((item) => String(item || "").trim()).filter(Boolean);
}

function splitCSVNames(value) {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function cellStyle(width) {
  if (!width) return { verticalAlign: "top" };
  const normalized = typeof width === "number" ? `${width}px` : width;
  return { width: normalized, minWidth: normalized, verticalAlign: "top" };
}

export default function PopupChecklistCol({
  colsize = 12,
  model = "md",
  margin = "mb-1",
  name,
  label = "",
  options = [],
  checklist = [],
  valueId = "id",
  labelId = "nama",
  validation,
  error,
  register,
  setValue,
  defaultValue = {},
  buttonLabel = "Pilih",
  dialogTitle = "",
  columns = [],
  perPage = 10,
  disabled = false,
  optionsUrl = "",
  requestParams = {},
  watch = () => undefined,
  parentField = "",
  parentOptions = [],
  parentValueId = "id_organisasi",
  parentChildIdsField = "id_cabor",
  parentChildNamesField = "cabor_name",
  value,
  addOnly = false,
}) {
  const [, , loadingOptions, axiosFetch] = useAxiosFunction();
  const initialValue = React.useMemo(() => {
    if (value !== undefined) return normalizeList(value);
    return normalizeList(defaultValue?.[name]);
  }, [defaultValue, name, value]);
  const initialValueKey = React.useMemo(
    () => JSON.stringify(initialValue),
    [initialValue],
  );
  const initialLockedValue = React.useMemo(
    () => normalizeList(defaultValue?.[name]),
    [defaultValue, name],
  );
  const initialLockedValueKey = React.useMemo(
    () => JSON.stringify(initialLockedValue),
    [initialLockedValue],
  );
  const appliedInitialKey = React.useMemo(
    () => `${initialValueKey}|${initialLockedValueKey}`,
    [initialLockedValueKey, initialValueKey],
  );
  const appliedInitialValueRef = React.useRef("");
  const [remoteOptions, setRemoteOptions] = React.useState([]);
  const parentValue = parentField ? watch(parentField) : "";
  const parentDerivedOptions = React.useMemo(() => {
    if (!parentField || !parentValue || !Array.isArray(parentOptions)) return [];
    const parent = parentOptions.find(
      (item) => String(item?.[parentValueId] || "") === String(parentValue),
    );
    const ids = normalizeList(parent?.[parentChildIdsField]);
    const names = splitCSVNames(parent?.[parentChildNamesField]);
    return ids.map((id, index) => {
      const label = names[index] || id;
      return {
        [valueId]: id,
        [labelId]: label,
        id_cabor: id,
        nama: label,
        __value: id,
        __label: label,
      };
    });
  }, [
    labelId,
    parentChildIdsField,
    parentChildNamesField,
    parentField,
    parentOptions,
    parentValue,
    parentValueId,
    valueId,
  ]);
  const sourceOptions = remoteOptions.length > 0
    ? remoteOptions
    : Array.isArray(options) && options.length > 0
      ? options
      : parentDerivedOptions.length > 0
        ? parentDerivedOptions
        : checklist;
  const [open, setOpen] = React.useState(false);
  const [keyword, setKeyword] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [sortCol, setSortCol] = React.useState("");
  const [sortDir, setSortDir] = React.useState("asc");
  const [selected, setSelected] = React.useState(initialValue);
  const [lockedSelected, setLockedSelected] = React.useState(addOnly ? initialValue : []);

  React.useEffect(() => {
    if (appliedInitialValueRef.current === appliedInitialKey) return;
    appliedInitialValueRef.current = appliedInitialKey;
    setSelected(initialValue);
    setLockedSelected(addOnly ? initialLockedValue : []);
    setValue(name, initialValue, { shouldValidate: false, shouldDirty: false });
  }, [addOnly, appliedInitialKey, initialLockedValue, initialValue, name, setValue]);

  React.useEffect(() => {
    if (!open || !optionsUrl) return;
    let mounted = true;
    axiosFetch({
      method: "GET",
      url: optionsUrl,
      auth: true,
      params: requestParams,
    }).then((resp) => {
      if (!mounted) return;
      setRemoteOptions(Array.isArray(resp?.data) ? resp.data : []);
      setPage(1);
    });
    return () => {
      mounted = false;
    };
  }, [axiosFetch, open, optionsUrl, JSON.stringify(requestParams)]);

  const getValue = React.useCallback((item) => String(item?.[valueId] || ""), [valueId]);
  const getLabel = React.useCallback((item) => String(item?.[labelId] || ""), [labelId]);

  const selectedSet = React.useMemo(() => new Set(selected.map(String)), [selected]);
  const allowedValueKey = React.useMemo(
    () => sourceOptions.map((item) => getValue(item)).filter(Boolean).join("|"),
    [getValue, sourceOptions],
  );
  const initialLabelMap = React.useMemo(() => {
    const ids = normalizeList(defaultValue?.[name]);
    const names = splitCSVNames(defaultValue?.[parentChildNamesField]);
    const labels = new Map();
    ids.forEach((id, index) => {
      if (names[index]) {
        labels.set(String(id), names[index]);
      }
    });
    return labels;
  }, [defaultValue, name, parentChildNamesField]);

  const selectedItems = React.useMemo(() => {
    const optionLabels = new Map();
    sourceOptions.forEach((item) => {
      const id = getValue(item);
      const itemLabel = getLabel(item);
      if (id && itemLabel) {
        optionLabels.set(id, itemLabel);
      }
    });
    return selected
      .map((id) => {
        const key = String(id);
        return {
          id: key,
          label: optionLabels.get(key) || initialLabelMap.get(key) || key,
        };
      })
      .filter((item) => item.id && item.label);
  }, [getLabel, getValue, initialLabelMap, selected, sourceOptions]);

  const setSelectedValue = React.useCallback((next) => {
    setSelected(next);
    setValue(name, next, { shouldValidate: true, shouldDirty: true });
  }, [name, setValue]);

  const removeSelected = React.useCallback((id) => {
    const key = String(id);
    setLockedSelected((current) => current.filter((item) => String(item) !== key));
    setSelectedValue(selected.filter((item) => String(item) !== key));
  }, [selected, setSelectedValue]);

  React.useEffect(() => {
    if (selected.length < 1) return;
    if (sourceOptions.length < 1) return;
    const allowed = new Set(sourceOptions.map((item) => getValue(item)).filter(Boolean));
    const next = selected.filter((item) => allowed.has(String(item)));
    if (next.length !== selected.length) {
      setSelectedValue(next);
    }
  }, [allowedValueKey, getValue, selected, setSelectedValue, sourceOptions]);

  const filteredRows = React.useMemo(() => {
    const q = keyword.trim().toLowerCase();
    const rows = q
      ? sourceOptions.filter((item) => {
          const values = [getLabel(item)].concat(columns.map((col) => item?.[col.name]));
          return values.some((value) => String(value || "").toLowerCase().includes(q));
        })
      : sourceOptions;
    const sorted = [...rows];
    if (sortCol) {
      sorted.sort((a, b) => {
        const av = String(a?.[sortCol] || "").toLowerCase();
        const bv = String(b?.[sortCol] || "").toLowerCase();
        if (av === bv) return 0;
        const result = av > bv ? 1 : -1;
        return sortDir === "asc" ? result : -result;
      });
    }
    return sorted;
  }, [columns, getLabel, keyword, sortCol, sortDir, sourceOptions]);

  const totalPage = Math.max(1, Math.ceil(filteredRows.length / perPage));
  const currentPage = Math.min(Number(page) || 1, totalPage);
  const pageRows = filteredRows.slice((currentPage - 1) * perPage, currentPage * perPage);
  const paging = { count: filteredRows.length, page: currentPage, total_page: totalPage };

  const toggleID = (id) => {
    const key = String(id);
    if (addOnly && selectedSet.has(key) && lockedSelected.map(String).includes(key)) {
      return;
    }
    const next = selectedSet.has(key)
      ? selected.filter((item) => String(item) !== key)
      : selected.concat(key);
    setSelectedValue(next);
  };

  const sortData = (col) => {
    if (col?.sortable === false) return;
    setPage(1);
    if (sortCol === col.name) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortCol(col.name);
    setSortDir("asc");
  };

  const sortClass = (col) => {
    if (col?.sortable === false) return "";
    if (sortCol !== col.name) return "sorting";
    return `sorting sorting_${sortDir}`;
  };

  const dialogColumns = columns.length > 0 ? columns : [{ name: labelId, label: label || "Data" }];

  return (
    <Col size={colsize} model={model} margin={margin}>
      <input type="hidden" {...register(name, validation)} />
      {label ? <label>{label}</label> : null}
      <div className={`border rounded p-2 ${error ? "border-danger" : ""}`}>
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-primary btn-sm flex-shrink-0"
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            <i className="fa fa-check-square mr-1" />
            {buttonLabel}
          </button>
          <div className="ml-2 d-flex flex-wrap align-items-center" style={{ gap: "4px" }}>
            {selectedItems.length > 0 ? selectedItems.map((item) => (
              <span key={item.id} className="badge badge-light border text-dark d-inline-flex align-items-center">
                <span>{item.label}</span>
                {!disabled ? (
                  <button
                    type="button"
                    className="close ml-1"
                    style={{ fontSize: "1rem", lineHeight: 1 }}
                    aria-label={`Hapus ${item.label}`}
                    onClick={() => removeSelected(item.id)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                ) : null}
              </span>
            )) : <span className="text-muted">Belum ada yang dipilih</span>}
          </div>
        </div>
      </div>
      {error ? <span className="error invalid-feedback d-block">{error}</span> : null}
      <PopupDialog
        title={dialogTitle || label || "Pilih Data"}
        size="xl"
        show={open}
        closeHandle={() => setOpen(false)}
        closeLabel="Tutup"
      >
        <div className="dataTables_wrapper dt-bootstrap4">
          <div className="row mb-3">
            <div className="col-md-4">
              <input
                className="form-control"
                placeholder="Pencarian..."
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 table-responsive">
              <table className="table sortable table-bordered table-hover table-striped table-condensed">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 70 }}>Pilih</th>
                    {dialogColumns.map((col) => (
                      <th
                        key={col.name}
                        className={`${col.center ? "text-center" : ""} ${sortClass(col)}`.trim()}
                        style={{ ...cellStyle(col.width), cursor: col?.sortable === false ? "default" : "pointer" }}
                        onClick={() => sortData(col)}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loadingOptions ? (
                    <tr>
                      <td colSpan={dialogColumns.length + 1} className="text-center text-muted">Memuat data...</td>
                    </tr>
                  ) : pageRows.length < 1 ? (
                    <tr>
                      <td colSpan={dialogColumns.length + 1} className="text-center text-muted">Data tidak tersedia</td>
                    </tr>
                  ) : (
                    pageRows.map((item) => {
                      const id = getValue(item);
                      return (
                        <tr key={id}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={selectedSet.has(id)}
                              disabled={addOnly && selectedSet.has(id) && lockedSelected.map(String).includes(id)}
                              onChange={() => toggleID(id)}
                            />
                          </td>
                          {dialogColumns.map((col) => (
                            <td key={col.name} className={col.center ? "text-center" : ""} style={cellStyle(col.width)}>
                              {col.render ? col.render(item) : item?.[col.name]}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DataPagination paging={paging} changePage={setPage} />
        </div>
      </PopupDialog>
    </Col>
  );
}
